import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ShoppingBag, 
  Loader2, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  FileText,
  CreditCard,
  Star,
  Crown,
  Eye,
  Calendar,
  Receipt,
  Download,
  ExternalLink
} from 'lucide-react';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyOrders = () => {
  const { formatCurrency } = useCountry();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { from: '/my-orders', message: 'Please login to view your orders' } });
      return;
    }
    fetchOrders();
  }, [navigate]);

  const fetchOrders = async (status = null) => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Please login to view your orders');
        return;
      }
      
      let url = `${API_URL}/api/payments/my-orders`;
      if (status && status !== 'all') {
        url += `?status=${status}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setOrders(data.orders);
        setStats(data.stats);
      } else {
        setError(data.detail || 'Failed to load orders');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'awaiting_payment': { label: 'Awaiting Payment', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      'proof_uploaded': { label: 'Proof Uploaded', color: 'bg-blue-100 text-blue-700', icon: FileText },
      'under_review': { label: 'Under Review', color: 'bg-purple-100 text-purple-700', icon: Eye },
      'verified': { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'rejected': { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
      'expired': { label: 'Expired', color: 'bg-gray-100 text-gray-700', icon: AlertCircle }
    };
    
    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-700', icon: AlertCircle };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getServiceIcon = (serviceType) => {
    if (serviceType?.includes('membership')) {
      return <Crown className="h-5 w-5 text-purple-600" />;
    } else if (serviceType?.includes('featured')) {
      return <Star className="h-5 w-5 text-yellow-600" />;
    }
    return <CreditCard className="h-5 w-5 text-blue-600" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatServiceType = (serviceType) => {
    if (!serviceType) return 'Service';
    return serviceType
      .replace(/_/g, ' ')
      .replace(/membership/i, 'Membership:')
      .replace(/featured/i, 'Featured Ad:')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderDetail(true);
  };

  const filterOrders = (tab) => {
    switch (tab) {
      case 'pending':
        return orders.filter(o => ['awaiting_payment', 'proof_uploaded', 'under_review'].includes(o.status));
      case 'approved':
        return orders.filter(o => o.status === 'verified');
      case 'rejected':
        return orders.filter(o => o.status === 'rejected');
      default:
        return orders;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <ShoppingBag className="h-10 w-10" />
            <div>
              <h1 className="text-4xl font-bold">My Orders</h1>
              <p className="text-blue-100">Track your payment history and purchases</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Receipt className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Orders</p>
                <p className="text-2xl font-bold">{stats.total || 0}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold">{stats.pending || 0}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Approved</p>
                <p className="text-2xl font-bold">{stats.approved || 0}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Spent</p>
                <p className="text-2xl font-bold">{formatCurrency((stats.total_spent || 0))}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Orders List */}
        <Card>
          <CardHeader>
            <CardTitle>Order History</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all" onValueChange={setActiveTab}>
              <TabsList className="mb-6">
                <TabsTrigger value="all">All ({stats.total || 0})</TabsTrigger>
                <TabsTrigger value="pending">Pending ({stats.pending || 0})</TabsTrigger>
                <TabsTrigger value="approved">Approved ({stats.approved || 0})</TabsTrigger>
                <TabsTrigger value="rejected">Rejected ({stats.rejected || 0})</TabsTrigger>
              </TabsList>

              {['all', 'pending', 'approved', 'rejected'].map((tab) => (
                <TabsContent key={tab} value={tab}>
                  {filterOrders(tab).length > 0 ? (
                    <div className="space-y-4">
                      {filterOrders(tab).map((order) => (
                        <Card key={order.order_id} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              {/* Order Info */}
                              <div className="flex items-start gap-4">
                                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                  {getServiceIcon(order.service_type)}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <h3 className="font-semibold">{order.service_title || formatServiceType(order.service_type)}</h3>
                                    {getStatusBadge(order.status)}
                                  </div>
                                  <p className="text-sm text-gray-500">
                                    Order ID: <span className="font-mono">{order.order_id}</span>
                                  </p>
                                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                    <span className="flex items-center gap-1">
                                      <Calendar className="h-4 w-4" />
                                      {formatDate(order.created_at)}
                                    </span>
                                    {order.service_duration && (
                                      <span>{order.service_duration} days</span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Amount & Actions */}
                              <div className="flex items-center gap-4 md:text-right">
                                <div>
                                  <p className="text-2xl font-bold text-blue-600">
                                    {formatCurrency((order.amount || 0))}
                                  </p>
                                  {order.status === 'verified' && order.verified_at && (
                                    <p className="text-xs text-green-600">
                                      Approved on {formatDate(order.verified_at)}
                                    </p>
                                  )}
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewDetails(order)}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  Details
                                </Button>
                              </div>
                            </div>

                            {/* Rejection Reason */}
                            {order.status === 'rejected' && order.rejection_reason && (
                              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-700">
                                  <strong>Rejection Reason:</strong> {order.rejection_reason}
                                </p>
                              </div>
                            )}

                            {/* Action for pending payments */}
                            {order.status === 'awaiting_payment' && (
                              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between">
                                <p className="text-sm text-yellow-700">
                                  Complete your payment to activate this service
                                </p>
                                <Link to={`/payment/bank-transfer/${order.order_id}`}>
                                  <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700">
                                    Complete Payment
                                  </Button>
                                </Link>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-600 mb-2">
                        {tab === 'all' ? 'No orders yet' : `No ${tab} orders`}
                      </h3>
                      <p className="text-gray-500 mb-6">
                        {tab === 'all' 
                          ? 'Start by purchasing a membership or featuring your ads'
                          : `You don't have any ${tab} orders`
                        }
                      </p>
                      {tab === 'all' && (
                        <div className="flex justify-center gap-4">
                          <Link to="/membership">
                            <Button className="bg-gradient-to-r from-blue-600 to-purple-600">
                              <Crown className="h-4 w-4 mr-2" />
                              View Memberships
                            </Button>
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Order Detail Modal */}
      <Dialog open={showOrderDetail} onOpenChange={setShowOrderDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Order Details
            </DialogTitle>
            <DialogDescription>
              Order ID: {selectedOrder?.order_id}
            </DialogDescription>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <span className="font-medium">Status</span>
                {getStatusBadge(selectedOrder.status)}
              </div>

              {/* Service Details */}
              <div>
                <h4 className="font-semibold mb-3">Service Details</h4>
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-500">Service</p>
                    <p className="font-medium">{selectedOrder.service_title || formatServiceType(selectedOrder.service_type)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Duration</p>
                    <p className="font-medium">{selectedOrder.service_duration || 'N/A'} days</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Amount</p>
                    <p className="font-medium text-blue-600">{formatCurrency((selectedOrder.amount || 0))}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Payment Method</p>
                    <p className="font-medium">Bank Transfer</p>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h4 className="font-semibold mb-3">Timeline</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Receipt className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">Order Created</p>
                      <p className="text-sm text-gray-500">{formatDate(selectedOrder.created_at)}</p>
                    </div>
                  </div>
                  
                  {selectedOrder.transfer_date && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        <FileText className="h-4 w-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">Payment Proof Uploaded</p>
                        <p className="text-sm text-gray-500">{formatDate(selectedOrder.transfer_date)}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedOrder.verified_at && (
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 ${selectedOrder.status === 'verified' ? 'bg-green-100' : 'bg-red-100'} rounded-full flex items-center justify-center`}>
                        {selectedOrder.status === 'verified' 
                          ? <CheckCircle className="h-4 w-4 text-green-600" />
                          : <XCircle className="h-4 w-4 text-red-600" />
                        }
                      </div>
                      <div>
                        <p className="font-medium">
                          {selectedOrder.status === 'verified' ? 'Payment Verified' : 'Payment Rejected'}
                        </p>
                        <p className="text-sm text-gray-500">{formatDate(selectedOrder.verified_at)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Bank Transfer Details */}
              {selectedOrder.selected_bank && (
                <div>
                  <h4 className="font-semibold mb-3">Payment Details</h4>
                  <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">Bank</p>
                      <p className="font-medium">{selectedOrder.selected_bank}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Reference</p>
                      <p className="font-medium font-mono">{selectedOrder.transfer_reference || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Proof */}
              {selectedOrder.proof_file_path && (
                <div>
                  <h4 className="font-semibold mb-3">Payment Proof</h4>
                  <a 
                    href={`${API_URL}${selectedOrder.proof_file_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <FileText className="h-5 w-5 text-blue-600" />
                    <span className="text-blue-600 font-medium">View Payment Proof</span>
                    <ExternalLink className="h-4 w-4 text-blue-600 ml-auto" />
                  </a>
                </div>
              )}

              {/* Rejection Reason */}
              {selectedOrder.status === 'rejected' && selectedOrder.rejection_reason && (
                <div>
                  <h4 className="font-semibold mb-3">Rejection Reason</h4>
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700">{selectedOrder.rejection_reason}</p>
                  </div>
                </div>
              )}

              {/* Additional Notes */}
              {selectedOrder.additional_notes && (
                <div>
                  <h4 className="font-semibold mb-3">Your Notes</h4>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-700">{selectedOrder.additional_notes}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyOrders;
